import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DNSProvider, DNSZone, DNSRecord, SystemHealth } from '../../types';

interface DNSState {
  providers: DNSProvider[];
  zones: DNSZone[];
  records: DNSRecord[];
  systemHealth: SystemHealth | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: DNSState = {
  providers: [],
  zones: [],
  records: [],
  systemHealth: null,
  isLoading: false,
  error: null,
};

const dnsSlice = createSlice({
  name: 'dns',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Providers
    setProviders: (state, action: PayloadAction<DNSProvider[]>) => {
      state.providers = action.payload;
    },
    addProvider: (state, action: PayloadAction<DNSProvider>) => {
      state.providers.push(action.payload);
    },
    updateProvider: (state, action: PayloadAction<DNSProvider>) => {
      const index = state.providers.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.providers[index] = action.payload;
      }
    },
    removeProvider: (state, action: PayloadAction<string>) => {
      state.providers = state.providers.filter((p) => p.id !== action.payload);
    },

    // Zones
    setZones: (state, action: PayloadAction<DNSZone[]>) => {
      state.zones = action.payload;
    },
    addZone: (state, action: PayloadAction<DNSZone>) => {
      state.zones.push(action.payload);
    },
    updateZone: (state, action: PayloadAction<DNSZone>) => {
      const index = state.zones.findIndex((z) => z.id === action.payload.id);
      if (index !== -1) {
        state.zones[index] = action.payload;
      }
    },
    removeZone: (state, action: PayloadAction<string>) => {
      state.zones = state.zones.filter((z) => z.id !== action.payload);
    },

    // Records
    setRecords: (state, action: PayloadAction<DNSRecord[]>) => {
      state.records = action.payload;
    },
    addRecord: (state, action: PayloadAction<DNSRecord>) => {
      state.records.push(action.payload);
    },
    updateRecord: (state, action: PayloadAction<DNSRecord>) => {
      const index = state.records.findIndex((r) => r.id === action.payload.id);
      if (index !== -1) {
        state.records[index] = action.payload;
      }
    },
    removeRecord: (state, action: PayloadAction<string>) => {
      state.records = state.records.filter((r) => r.id !== action.payload);
    },

    // Health
    setSystemHealth: (state, action: PayloadAction<SystemHealth>) => {
      state.systemHealth = action.payload;
    },
  },
});

export const {
  setLoading,
  setError,
  setProviders,
  addProvider,
  updateProvider,
  removeProvider,
  setZones,
  addZone,
  updateZone,
  removeZone,
  setRecords,
  addRecord,
  updateRecord,
  removeRecord,
  setSystemHealth,
} = dnsSlice.actions;

export default dnsSlice.reducer;
